import {
  HYDROFORECAST_ALERT_TYPE_LONG_TERM_AVERAGE_COMPARISON,
  HYDROFORECAST_ALERT_TYPE_THRESHOLD,
  HYDROFORECAST_ALERT_TYPE_UPCOMING_EVENT,
} from 'src/lib/constants';

import {getAllPages, makeApiRequest} from './api';
import {ApiNotificationPreference} from './preferences';
import {Project, Site} from './project';
import {AllColumns, Source} from './series';

export type AlertConfigType =
  | typeof HYDROFORECAST_ALERT_TYPE_THRESHOLD
  | typeof HYDROFORECAST_ALERT_TYPE_LONG_TERM_AVERAGE_COMPARISON
  | typeof HYDROFORECAST_ALERT_TYPE_UPCOMING_EVENT;

export type ForecastThresholdAlertConfigProperties = {
  source: Source;
  column: AllColumns;
  direction: 'above' | 'below';
  threshold: number;
  rateVolumeMode?: 'rate' | 'level';
  label?: string;
  showOnPlot?: 'self' | 'everyone' | null;
  plotColor?: string;
};

export type UpcomingEventAlertConfigProperties = {
  source: Source;
  column: AllColumns;
  threshold: number;
  direction: 'above';
  noise_floor_percent_lta: number;
  label?: string;
};

export type ApiAlertConfigBase = {
  id: number;
  projectId: Project['id'];
  siteIds: Site['id'][];
  alertType: AlertConfigType;
  properties: ForecastThresholdAlertConfigProperties | UpcomingEventAlertConfigProperties;
  createdBy: string;
  createdByName: string;
  units?: string;
};

export interface ApiForecastThresholdAlertConfig extends ApiAlertConfigBase {
  properties: ForecastThresholdAlertConfigProperties;
  alertType:
    | typeof HYDROFORECAST_ALERT_TYPE_THRESHOLD
    | typeof HYDROFORECAST_ALERT_TYPE_LONG_TERM_AVERAGE_COMPARISON;
}

export interface ApiUpcomingEventAlertConfig extends ApiAlertConfigBase {
  properties: UpcomingEventAlertConfigProperties;
  alertType: typeof HYDROFORECAST_ALERT_TYPE_UPCOMING_EVENT;
}

export type ApiAlertConfig = ApiForecastThresholdAlertConfig | ApiUpcomingEventAlertConfig;

export async function listAlertConfigs(): Promise<ApiAlertConfig[]> {
  const {data: alertConfigs} = await getAllPages(
    (params) =>
      makeApiRequest<ApiAlertConfig[]>('/hydroforecast_alert_configs', {
        ...params,
      }),
    {},
    2
  );

  return alertConfigs;
}

export type NewAlertConfig = Pick<
  ApiAlertConfig,
  'projectId' | 'siteIds' | 'alertType' | 'properties'
> &
  Pick<ApiNotificationPreference, 'ccEmails'>;

export type CreateAlertConfigResponse = {
  alertConfig: ApiAlertConfig;
  notificationPreference: ApiNotificationPreference;
};

export async function createAlertConfig(data: NewAlertConfig): Promise<CreateAlertConfigResponse> {
  const response = await makeApiRequest<CreateAlertConfigResponse>(
    `hydroforecast_alert_configs`,
    null,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  return response.data;
}

export async function updateAlertConfig(
  id: ApiAlertConfig['id'],
  properties: ForecastThresholdAlertConfigProperties | UpcomingEventAlertConfigProperties,
  siteIds?: Site['id'][]
): Promise<ApiAlertConfig> {
  const response = await makeApiRequest<ApiAlertConfig>(`hydroforecast_alert_configs/${id}`, null, {
    method: 'PUT',
    body: JSON.stringify({
      properties,
      siteIds,
    }),
  });
  return response.data;
}

export async function deleteAlertConfig(id: ApiAlertConfig['id']): Promise<ApiAlertConfig> {
  const response = await makeApiRequest<ApiAlertConfig>(`hydroforecast_alert_configs/${id}`, null, {
    method: 'DELETE',
  });
  return response.data;
}
